import { ref } from 'vue';

import * as DataDogLogs from '~/lib/datadog/datadog';

// Module-level constants and state for the scroll locking mechanism
const SELECTOR_FIXED_CONTENT = '.fixed-top, .fixed-bottom, .is-fixed, .sticky-top';
const SELECTOR_STICKY_CONTENT = '.sticky-top';
const PROPERTY_PADDING_RIGHT = 'paddingRight';
const PROPERTY_MARGIN_RIGHT = 'marginRight';
const PROPERTY_OVERFLOW = 'overflow';
const DATA_ATTRIBUTE_PREFIX = 'bsScrollLock'; // Prefix for data attributes

let activeLockCount = 0;
const verticalScroll = ref(0);

/**
 * Gets the scrollbar width.
 * @returns {number} The width of the scrollbar in pixels.
 */
const getScrollbarWidth = () => {
  // https://developer.mozilla.org/en-US/docs/Web/API/Window/innerWidth#usage_notes
  const documentWidth = document.documentElement.clientWidth;
  return Math.abs(window.innerWidth - documentWidth);
};

/**
 * Checks if the page is currently overflowing (has a visible scrollbar).
 * @returns {boolean} True if overflowing, false otherwise.
 */
const isPageOverflowing = () => {
  return getScrollbarWidth() > 0;
};

/**
 * Saves the initial inline style of an element's property to a data attribute.
 * @param {HTMLElement} element The element.
 * @param {string} propertyName The CSS property name (e.g., 'paddingRight', 'overflow').
 */
const saveInitialAttribute = (element, propertyName) => {
  const datasetKey = `${DATA_ATTRIBUTE_PREFIX}${propertyName.charAt(0).toUpperCase() + propertyName.slice(1)}`;
  const currentValue = element.style[propertyName];
  // Only store if there's an existing inline style, otherwise, on reset, we'll just remove our added style.
  if (currentValue) {
    element.dataset[datasetKey] = currentValue;
  }
};

/**
 * Resets an element's property to its saved initial style or removes the inline style if none was saved.
 * @param {HTMLElement} element The element.
 * @param {string} propertyName The CSS property name.
 */
const resetAttribute = (element, propertyName) => {
  const datasetKey = `${DATA_ATTRIBUTE_PREFIX}${propertyName.charAt(0).toUpperCase() + propertyName.slice(1)}`;
  const originalValue = element.dataset[datasetKey];

  if (typeof originalValue === 'string') {
    // An original inline style was saved
    element.style[propertyName] = originalValue;
    delete element.dataset[datasetKey];
  } else {
    // No original inline style was saved, so remove the style we added
    // Convert camelCase (e.g., paddingRight) to kebab-case (e.g., padding-right) for removeProperty
    const kebabCasePropertyName = propertyName.replace(/([A-Z])/g, '-$1').toLowerCase();
    element.style.removeProperty(kebabCasePropertyName);
  }
};

/**
 * Sets style attributes on elements, adjusting for scrollbar width.
 * It saves the initial style before modification.
 * @param {HTMLElement | string} selectorOrElement Element or selector string.
 * @param {string} propertyName CSS property to set (e.g., 'paddingRight').
 * @param {(currentValue: number) => number} valueCallback Callback to compute new numeric value for the style (in px).
 */
const setElementStyleWithAdjustment = (selectorOrElement, propertyName, valueCallback) => {
  const elements =
    typeof selectorOrElement === 'string'
      ? Array.from(document.querySelectorAll(selectorOrElement))
      : [selectorOrElement];

  elements.forEach((element) => {
    saveInitialAttribute(element, propertyName); // Save before changing
    const computedStyleValue = window.getComputedStyle(element)[propertyName];
    const currentValue = parseFloat(computedStyleValue) || 0;
    element.style[propertyName] = `${valueCallback(currentValue)}px`;
  });
};

/**
 * Scroll Lock Composable (Bootstrap-style)
 *
 * This composable provides utilities to lock and unlock page scrolling,
 * compensating for scrollbar width to prevent layout shifts.
 * It's useful for modals, drawers, and other overlay components.
 *
 * @returns {Object} Object containing { lockScroll, unlockScroll } methods.
 */
export function useScrollLock() {
  /**
   * Locks the page scroll.
   * Prevents body scrolling and adjusts padding/margins to compensate for scrollbar removal.
   */
  const lockScroll = () => {
    try {
      verticalScroll.value = window.scrollY; // Capture scroll position for this instance

      activeLockCount++;
      if (activeLockCount > 1) {
        // Already locked by another instance using this system, no style changes needed by this call.
        return;
      }

      // First active lock: apply style changes
      if (isPageOverflowing()) {
        const scrollbarWidth = getScrollbarWidth();

        saveInitialAttribute(document.body, PROPERTY_OVERFLOW);
        document.body.style.overflow = 'hidden';

        setElementStyleWithAdjustment(
          document.body,
          PROPERTY_PADDING_RIGHT,
          (value) => value + scrollbarWidth,
        );

        document.querySelectorAll(SELECTOR_FIXED_CONTENT).forEach((el) => {
          setElementStyleWithAdjustment(
            el,
            PROPERTY_PADDING_RIGHT,
            (value) => value + scrollbarWidth,
          );
        });

        document.querySelectorAll(SELECTOR_STICKY_CONTENT).forEach((el) => {
          setElementStyleWithAdjustment(
            el,
            PROPERTY_MARGIN_RIGHT,
            (value) => value - scrollbarWidth,
          );
        });
      } else {
        // No scrollbar, but still lock scroll by hiding overflow
        saveInitialAttribute(document.body, PROPERTY_OVERFLOW);
        document.body.style.overflow = 'hidden';
      }
    } catch (error) {
      DataDogLogs.error(
        '[Allbirds composable] - useScrollLock - lockScroll - Error trying to lock scroll',
        { activeLockCount },
        error,
      );
      if (activeLockCount > 0 && activeLockCount === 1) {
        // Decrement if this was the first locker and failed
        activeLockCount--;
      } else if (activeLockCount > 0) {
        activeLockCount--; // general decrement on error after increment
      }
    }
  };

  /**
   * Unlocks the page scroll.
   * Restores original body scrolling and styles, and the saved scroll position.
   */
  const unlockScroll = () => {
    try {
      if (activeLockCount === 0) {
        // Only restore scroll position if it's valid (greater than 0)
        if (verticalScroll.value > 0) {
          window.scrollTo(0, verticalScroll.value);
        }
        return;
      }

      activeLockCount--;

      if (activeLockCount === 0) {
        // Last active lock is being released: revert style changes
        resetAttribute(document.body, PROPERTY_PADDING_RIGHT);
        document.querySelectorAll(SELECTOR_FIXED_CONTENT).forEach((el) => {
          resetAttribute(el, PROPERTY_PADDING_RIGHT);
        });
        document.querySelectorAll(SELECTOR_STICKY_CONTENT).forEach((el) => {
          resetAttribute(el, PROPERTY_MARGIN_RIGHT);
        });
        resetAttribute(document.body, PROPERTY_OVERFLOW);
      }

      // Only restore scroll position if it's valid (greater than 0)
      if (verticalScroll.value > 0) {
        window.scrollTo(0, verticalScroll.value);
      }
    } catch (error) {
      DataDogLogs.error(
        '[Allbirds composable] - useScrollLock - unlockScroll - Error trying to unlock scroll',
        { activeLockCount },
        error,
      );
    }
  };

  return {
    lockScroll,
    unlockScroll,
  };
}
